import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2, Car, Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useQuote } from "@/lib/quote";
import DateField from "@/components/DateField";
import SearchSelect from "@/crm/components/SearchSelect";
import PpfBuilder from "@/components/PpfBuilder";
import { promoEligible, promoPrice, promoActive, promoServicePcts, bundlePriceInfo, fmtMoney } from "@/lib/promo";
import { CONTACT } from "@/lib/content";
import { track } from "@/lib/analytics";
import { vehicleLabel } from "@/lib/utils";

/**
 * Vehicle-first price calculator.
 * Year → Make → Model → server-side vehicle class → class-based price.
 * Displayed totals always come from POST /api/pricing/quote (server-authoritative).
 */

const sel =
  "w-full bg-white/5 border border-white/12 rounded-xl px-4 py-3.5 text-white text-sm outline-none focus:border-[#1F5EFF] transition-colors appearance-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

const money = fmtMoney;

function priceLabel(svc, vclass) {
  // Option-priced services (e.g. headlight one/pair): "$129 one / $199 pair"
  if (svc.options?.length) {
    return { text: svc.options.map((o) => `${money(o.price)} ${o.key}`).join(" / "), kind: "fixed" };
  }
  const kind = svc.price_kind;
  if (kind === "flat") return svc.flat_price === 0
    ? { text: "Free", kind: "fixed" }
    : { text: money(svc.flat_price) + (svc.unit ? ` / ${svc.unit}` : ""), kind: "fixed" };
  if (kind === "flat_starting") return { text: `Starting at ${money(svc.flat_price)}`, kind: "starting" };
  // missing class price → custom quote (any class, incl. D5 without a set price)
  const p = svc.prices?.[vclass];
  if (p == null) return { text: "Custom Quote Required", kind: "custom" };
  return kind === "class_starting"
    ? { text: `Starting at ${money(p)}`, kind: "starting" }
    : { text: money(p), kind: "fixed" };
}

function fromLabel(svc) {
  // shown before vehicle is selected
  if (svc.options?.length) {
    return { text: svc.options.map((o) => `${money(o.price)} ${o.key}`).join(" / ") };
  }
  if (svc.price_kind === "flat") return { text: money(svc.flat_price) + (svc.unit ? ` / ${svc.unit}` : "") };
  if (svc.price_kind === "flat_starting") return { text: `Starting at ${money(svc.flat_price)}` };
  const vals = Object.values(svc.prices || {}).filter((v) => v != null);
  if (!vals.length) return { text: "Custom Quote" };
  const min = Math.min(...vals);
  return svc.price_kind === "class_starting"
    ? { text: `Starting at ${money(min)}` }
    : { text: `From ${money(min)}` };
}

// Resolve an add-on price that may be a scalar or a per-class dict
function addonPrice(entry, vclass) {
  if (entry.custom) return { text: "Inspection", custom: true };
  const p = entry.prices ? (vclass ? entry.prices[vclass] : null) : entry.price;
  if (p == null) return vclass && entry.prices ? { text: "Custom", custom: true } : { text: "priced for your vehicle", custom: false };
  return { text: `+${money(p)}`, custom: false };
}

// Preferred category order for the service picker (fallback: config order)
const GROUP_ORDER = [
  "Detailing", "Paint Correction", "Ceramic Coating",
  "Protection & Add-Ons", "Headlights", "Odor Treatment", "Bed Liner",
];

// Client-facing category cards (step 02). Hidden services (e.g. Window Tint)
// are simply not listed — nothing about availability is promised.
const CATEGORY_META = {
  "Detailing": { label: "Detailing", blurb: "Interior, exterior & full details" },
  "Paint Correction": { label: "Paint Correction", blurb: "Gloss, swirls & defect removal" },
  "Ceramic Coating": { label: "Ceramic Protection", blurb: "Long-term hydrophobic protection" },
  "Protection & Add-Ons": { label: "Protection & Add-ons", blurb: "Glass, wheels, trim, engine bay" },
  "Headlights": { label: "Headlight Restoration", blurb: "Clarity restoration & sealing" },
  "Odor Treatment": { label: "Odor Treatment", blurb: "Source removal, not masking" },
  "Bed Liner": { label: "Bed Liner", blurb: "Spray-on bed protection" },
};
const UNAVAILABLE_CATEGORIES = [];

// Good / Better / Best tier cards — presentational grouping ONLY. Every tier
// maps to existing services/add-ons; all prices still come from the server.
const TIER_GROUPS = {
  "Detailing": [
    { label: "Essential", tag: "Good", hint: "Regular Interior — vacuum, panels, glass, mats, ~1.5–2 h. For routine maintenance.", service: "interior-regular" },
    { label: "Deep Clean", tag: "Better", hint: "Deep Interior Cleaning — shampoo extraction & steam included, ~3–4 h, min. two technicians. For stains and heavily soiled interiors.", service: "interior-deep" },
    { label: "Complete Reset", tag: "Best", hint: "Deep Clean + leather care + fabric extraction", service: "interior-deep", addons: ["leather_clean_condition", "seat_fabric_extraction"] },
  ],
  "Paint Correction": [
    { label: "One-Step Correction", tag: "Better", hint: "Noticeable gloss + light swirl removal", service: "paint-correction-1step" },
    { label: "Multi-Stage Correction", tag: "Best", hint: "Maximum safe defect removal", service: "paint-correction-multi" },
  ],
  "Ceramic Coating": [
    { label: "1-Year Protection", tag: "With Correction", hint: "Light ceramic applied right after a One-Step Correction", service: "paint-correction-1step", addons: ["pc_ceramic_1y"] },
    { label: "3-Year Ceramic", tag: "Good", hint: "WTD 3-Year — hydrophobic shine, up to 3 years", service: "ceramic-3yr" },
    { label: "5-Year Ceramic", tag: "Most Popular", hint: "WTD 5-Year — long-term hydrophobic protection", service: "ceramic-5yr" },
    { label: "7-Year Ceramic", tag: "Best", hint: "WTD 7-Year — our highest level of ceramic protection", service: "ceramic-7yr" },
  ],
};

// Short customer-facing fine print shown under the tier cards — mirrors the
// wording on the service pages. Copy only; no pricing logic.
const GROUP_NOTES = {
  "Ceramic Coating": "Full preparation — wash, decontamination and light one-step correction — is included in every ceramic price. Severe paint defects or multi-stage correction are quoted separately. Your vehicle may need to stay overnight for curing.",
  "Detailing": "Heavy soiling of the headliner and seats may add a surcharge based on severity — always confirmed at drop-off.",
};

// Highlighted upsell card (e.g. 1-Year Ceramic Protection after Paint Correction).
// Copy, prices and cross-sell link all come from the server pricing config.
function UpsellCard({ addon, vclass, added, onToggle, promoPct = null, pricePrefix = "Add with Paint Correction — " }) {
  const u = addon.upsell || {};
  const flat = Number.isFinite(addon.price) ? addon.price : null;
  const bundled = (vclass ? addon.prices?.[vclass] ?? null : null) ?? flat;
  const standalone = vclass ? addon.standalone_prices?.[vclass] ?? null : null;
  const minOf = (d) => Math.min(...Object.values(d || {}).filter((v) => v != null));
  return (
    <div data-testid={`calc-upsell-${addon.key}`}
      className={`mt-4 rounded-2xl border p-5 md:p-6 transition-colors duration-300 ${
        added
          ? "border-[#1F5EFF] bg-[#1F5EFF]/12"
          : "border-[#1F5EFF]/40 bg-gradient-to-br from-[#1F5EFF]/12 via-[#121216] to-[#121216]"}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="px-2.5 py-1 rounded-full bg-[#1F5EFF] text-white font-mono-alt text-[9px] uppercase tracking-widest">
          {u.badge || "Recommended Add-On"}
        </span>
      </div>
      <div className="font-display text-xl md:text-2xl text-white">{u.headline || addon.label}</div>
      {u.subheadline && (
        <div className="mt-1 font-mono-alt text-[11px] text-[#8db2ff] uppercase tracking-widest" data-testid="upsell-subheadline">
          {u.subheadline}
        </div>
      )}
      {u.description && (
        <p className="mt-2 text-[13px] text-white/60 leading-relaxed">{u.description}</p>
      )}
      {Array.isArray(u.benefits) && u.benefits.length > 0 && (
        <ul className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {u.benefits.map((b) => (
            <li key={b} className="flex items-start gap-2 text-[12px] text-white/75 leading-snug">
              <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-300" strokeWidth={2.5} />
              {b}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          {u.supporting_label ? (
            <div className="text-[11px] text-emerald-300/80 font-mono-alt uppercase tracking-widest" data-testid="upsell-supporting">
              {u.supporting_label}
            </div>
          ) : standalone != null ? (
            <div className="text-[11px] text-white/40" data-testid="upsell-standalone">
              Regular standalone price <span className="line-through">{money(standalone)}</span>
            </div>
          ) : Number.isFinite(minOf(addon.standalone_prices)) && (
            <div className="text-[11px] text-white/40" data-testid="upsell-standalone">
              Regular standalone price from {money(minOf(addon.standalone_prices))}
            </div>
          )}
          <div className="mt-0.5 text-base md:text-lg text-white font-medium" data-testid="upsell-price">
            {pricePrefix}
            {bundled != null && promoPct != null ? (
              <>
                <span className="line-through text-white/30 mr-1.5">+{money(bundled)}</span>
                <span className="text-emerald-300 tabular-nums">+{money(promoPrice(bundled, promoPct))}</span>
                <span className="ml-1.5 text-emerald-300 font-mono-alt text-[10px] uppercase">−{promoPct}% with your package</span>
              </>
            ) : (
              <span className="text-[#8db2ff] tabular-nums">
                {bundled != null ? `+${money(bundled)}` : `from ${money(minOf(addon.prices))}`}
              </span>
            )}
          </div>
        </div>
        <button type="button" onClick={onToggle} data-testid={`calc-upsell-toggle-${addon.key}`}
          className={`w-full sm:w-auto px-5 py-3 rounded-xl text-sm font-semibold transition-colors ${
            added
              ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-300 hover:bg-red-500/10 hover:border-red-400/40 hover:text-red-300"
              : "bg-[#1F5EFF] text-white hover:bg-[#8db2ff] hover:text-black"}`}>
          {added ? "✓ Added — tap to remove" : (u.cta_add || `+ Add ${addon.label}`)}
        </button>
      </div>
      {u.cross_sell_url && (
        <a href={u.cross_sell_url} data-testid="upsell-cross-sell"
          className="inline-block mt-3.5 text-[11px] text-[#8db2ff]/70 hover:text-[#8db2ff] underline underline-offset-2 transition-colors">
          {u.cross_sell_text}
        </a>
      )}
    </div>
  );
}

export default function Calculator({ presetServiceKey = null, presetNonce = 0, ppfFirst = false } = {}) {
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  // Selection survives page refresh (sessionStorage, this tab only)
  const persisted = (() => {
    try { return JSON.parse(sessionStorage.getItem("wtd_calc") || "{}"); } catch { return {}; }
  })();
  const [config, setConfig] = useState(null);
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [year, setYear] = useState(persisted.year || "");
  const [make, setMake] = useState(persisted.make || "");
  const [model, setModel] = useState(persisted.model || "");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [classified, setClassified] = useState(null); // {vehicle_class}
  const [serviceKeys, setServiceKeys] = useState(persisted.serviceKeys || []); // multi-select, max 4
  const [addonSel, setAddonSel] = useState(persisted.addonSel || {}); // {pet_hair: 'moderate'}
  const [addonFlags, setAddonFlags] = useState(persisted.addonFlags || []); // ['wheel_protection']
  const [serviceOptions, setServiceOptions] = useState(persisted.serviceOptions || {}); // {service_key: option_key}
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null); // {closed, duration_min, slots:[{time, available}]}
  const [slotsLoading, setSlotsLoading] = useState(false);
  // Accordion sections — Detailing open by default; toggling never touches selections
  // Progressive disclosure: pick a need (category) first, then the service.
  const [activeGroup, setActiveGroup] = useState(() => {
    // Deep link: /calculator?cat=ppf opens the PPF builder directly
    try {
      const c = new URLSearchParams(window.location.search).get("cat");
      if (c === "ppf") return "ppf";
    } catch { /* no query */ }
    // Dedicated PPF page: the PPF builder leads, other services stay optional
    return ppfFirst ? "ppf" : null;
  });
  // Popular-option cards (PpfPopular) preselect into the PPF builder
  const [ppfPreset, setPpfPreset] = useState(null); // {payload, n}
  // On the PPF page, detailing upsells/bundles wait until PPF coverage is set
  const [ppfConfigured, setPpfConfigured] = useState(false);
  useEffect(() => {
    const onPreselect = (e) => {
      setActiveGroup("ppf");
      setPpfPreset({ payload: e.detail || {}, n: Date.now() });
    };
    window.addEventListener("wtd-ppf-preselect", onPreselect);
    // "Add Paint Decontamination — From $71" CTA on the Exterior page:
    // selects Exterior Detail + the paint_decon add-on, exactly as configured.
    const onAddon = (e) => {
      if (!config) return;
      const svcKey = e.detail?.service || "exterior-detail";
      const addonKey = e.detail?.addon || "paint_decon";
      setServiceKeys((p) => (p.includes(svcKey) ? p : [...p, svcKey]));
      setAddonFlags((f) => [...new Set([...f, addonKey])]);
      const g = config.services.find((s) => s.key === svcKey)?.group;
      if (g) setActiveGroup(g);
      track("addon_preselect", { key: addonKey, service: svcKey });
    };
    window.addEventListener("wtd-addon-preselect", onAddon);
    return () => {
      window.removeEventListener("wtd-ppf-preselect", onPreselect);
      window.removeEventListener("wtd-addon-preselect", onAddon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Service-page CTA: "Add Paint Decontamination — From $71" selects the
  // Exterior Detail + the decon add-on in the configurator.
  useEffect(() => {
    const onAddon = (e) => {
      const { service, addon } = e.detail || {};
      if (!service || !config) return;
      const svc = config.services.find((s) => s.key === service);
      if (!svc) return;
      setServiceKeys((p) => (p.includes(service) ? p : [...p, service].slice(0, 4)));
      if (svc.group) setActiveGroup(svc.group);
      if (addon) setAddonFlags((f) => (f.includes(addon) ? f : [...f, addon]));
    };
    window.addEventListener("wtd-addon-preselect", onAddon);
    return () => window.removeEventListener("wtd-addon-preselect", onAddon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);
  // Exterior page CTA: pre-select the Exterior Detail + Paint Decontamination
  // add-on ("Add Paint Decontamination — From $71" button).
  useEffect(() => {
    const onPreset = (e) => {
      const d = e.detail || {};
      if (!d.service) return;
      setServiceKeys([d.service]);
      setAddonFlags((f) => [...new Set([...f, ...(d.addons || [])])]);
      setActiveGroup(d.group || null);
    };
    window.addEventListener("wtd-calc-preset", onPreset);
    return () => window.removeEventListener("wtd-calc-preset", onPreset);
  }, []);
  const viewedRef = useRef(new Set()); // analytics: viewed-once tracking

  // After a session restore, reopen the category of the first selected service
  useEffect(() => {
    if (config && serviceKeys.length && !activeGroup) {
      const g = config.services.find((s) => s.key === serviceKeys[0])?.group;
      if (g) setActiveGroup(g);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => {
      setConfig(data);
      // drop session-restored selections that no longer exist in the catalog
      const keys = new Set(data.services.map((s) => s.key));
      const akeys = new Set(Object.keys(data.addons || {}));
      setServiceKeys((p) => p.filter((k) => keys.has(k)));
      setAddonFlags((p) => p.filter((k) => akeys.has(k)));
      setAddonSel((p) => Object.fromEntries(Object.entries(p).filter(([k]) => akeys.has(k))));
    }).catch(() => {});
    api.get("/vehicles/years").then(({ data }) => setYears(data)).catch(() => {});
    api.get("/vehicles/makes").then(({ data }) => setMakes(data)).catch(() => {});
  }, []);

  // External preset (service pages): a CTA click bumps presetNonce; select
  // exactly that service and open its category. Skipped on initial mount
  // (nonce 0) so session restore is untouched. Add-on pruning for the new
  // selection is handled by the [serviceKeys, config] effect below.
  useEffect(() => {
    if (!config || !presetServiceKey || !presetNonce) return;
    const svc = config.services.find((s) => s.key === presetServiceKey);
    if (!svc) return;
    setServiceKeys([presetServiceKey]);
    if (svc.group) setActiveGroup(svc.group);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetNonce, config]);

  // load models when year+make chosen; keep the current model if it's still
  // valid for the list (session restore + StrictMode-safe)
  useEffect(() => {
    setModels([]);
    if (!year || !make) {
      setModel("");
      return;
    }
    setModelsLoading(true);
    api.get("/vehicles/models", { params: { year, make } })
      .then(({ data }) => {
        setModels(data);
        setModel((m) => (m && data.includes(m) ? m : ""));
      })
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [year, make]);

  // persist the build for this tab session
  useEffect(() => {
    try {
      sessionStorage.setItem("wtd_calc", JSON.stringify({ year, make, model, serviceKeys, addonSel, addonFlags, serviceOptions }));
    } catch { /* storage full/blocked — ignore */ }
  }, [year, make, model, serviceKeys, addonSel, addonFlags, serviceOptions]);

  // classify when model chosen
  useEffect(() => {
    setClassified(null);
    if (!year || !make || !model) return;
    api.post("/vehicles/classify", { year: Number(year), make, model })
      .then(({ data }) => setClassified(data))
      .catch(() => setClassified(null));
  }, [year, make, model]);

  const selectedServices = useMemo(
    () => (config ? serviceKeys.map((k) => config.services.find((s) => s.key === k)).filter(Boolean) : []),
    [config, serviceKeys]
  );
  const service = selectedServices[0] || null; // primary — legacy single-service bits

  // Toggle a service on/off (max 4). Add-ons that no longer apply to any
  // remaining service are pruned; everything else survives accordion toggles.
  const toggleService = (key) => {
    setServiceKeys((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : prev.length >= 4 ? prev : [...prev, key];
      return next;
    });
  };

  useEffect(() => {
    if (!config) return;
    const selected = new Set(serviceKeys);
    setServiceOptions((o) => Object.fromEntries(Object.entries(o).filter(([k]) => selected.has(k))));
    setAddonSel((s) => Object.fromEntries(Object.entries(s).filter(([k]) => {
      const a = config.addons[k];
      return a && (a.applies_to || []).some((t) => selected.has(t));
    })));
    setAddonFlags((f) => f.filter((k) => {
      const a = config.addons[k];
      return a && (a.applies_to || []).some((t) => selected.has(t));
    }));
    setDate("");
    setTime("");
    setSlots(null);
  }, [serviceKeys, config]);

  // Per-service add-on lists (upsell cards excluded — they render separately).
  // Each add-on renders once, under the first selected service that offers it.
  const addonsByService = useMemo(() => {
    if (!config) return [];
    const seen = new Set();
    return selectedServices.map((svc) => {
      const includedSet = new Set(svc.included_addons || []);
      const list = (svc.addon_keys || [])
        .map((k) => config.addons[k])
        .filter((a) => a && !a.upsell && !seen.has(a.key) && seen.add(a.key))
        .map((a) => ({ ...a, _included: includedSet.has(a.key) }));
      return { service: svc, addons: list };
    }).filter((x) => x.addons.length > 0);
  }, [config, selectedServices]);

  // Upsell add-on (e.g. 1-Year Ceramic Protection) from any selected service
  const upsellAddon = useMemo(() => {
    if (!config) return null;
    for (const svc of selectedServices) {
      const a = (svc.addon_keys || []).map((k) => config.addons[k]).find((x) => x && x.upsell);
      if (a) return a;
    }
    return null;
  }, [config, selectedServices]);

  const vclass = classified?.vehicle_class || null;
  const bundles = useMemo(() => (config?.bundles || []), [config]);
  // Price used for promo ranking — option-priced services use the chosen
  // option (min option until chosen), mirroring the backend's actual base.
  const promoRankPrice = (s) => {
    let price = vclass ? s.prices?.[vclass] ?? null : null;
    if (price == null && s.flat_price != null) price = s.flat_price;
    if (price == null && s.options?.length) {
      const opt = s.options.find((o) => o.key === serviceOptions[s.key]);
      price = opt ? opt.price : Math.min(...s.options.map((o) => o.price));
    }
    return price;
  };
  // Per-service promo % for the current selection (GO v2: 50% only to the
  // most expensive eligible service; secondary % to the rest) — mirrors backend.
  const promoPcts = useMemo(
    () => promoServicePcts(config?.promo, selectedServices.map((s) => ({ key: s.key, price: promoRankPrice(s) }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, selectedServices, vclass, serviceOptions]
  );
  const selForPromo = selectedServices.map((s) => ({ key: s.key, price: promoRankPrice(s) }));
  // Card display %: actual when selected; otherwise what the service WOULD get
  // if added to the current selection — updates dynamically as services change.
  const pctFor = (key, price) => {
    if (promoPcts[key] != null) return promoPcts[key];
    const p = promoServicePcts(config?.promo, [...selForPromo, { key, price }]);
    return p[key] ?? (Number(config?.promo?.percent) || 50);
  };
  // A package counts as added when ALL its services + add-on flags are selected.
  const isBundleAdded = (b) =>
    (b.services || []).every((k) => serviceKeys.includes(k)) &&
    (b.addon_flags || []).every((f) => addonFlags.includes(f));
  const addedBundles = bundles.filter(isBundleAdded);
  // Only "maximal" packages go to the server — a package fully contained in
  // another added package (Paint Revival ⊂ Full Vehicle Refresh) shows as
  // added but is never billed twice.
  const payloadBundles = addedBundles.filter((b) =>
    !addedBundles.some((o) => o.key !== b.key &&
      (b.services || []).every((k) => (o.services || []).includes(k)) &&
      (b.addon_flags || []).every((f) => (o.addon_flags || []).includes(f))));
  // Add = merge contents (a service can be in the order only once);
  // remove = drop this package's contents, keeping anything still covered
  // by another added package.
  const toggleBundle = (b) => {
    if (isBundleAdded(b)) {
      const keepSvc = new Set();
      const keepFlags = new Set();
      for (const other of bundles) {
        if (other.key === b.key || !isBundleAdded(other)) continue;
        (other.services || []).forEach((k) => keepSvc.add(k));
        (other.addon_flags || []).forEach((f) => keepFlags.add(f));
      }
      const dropSvc = new Set((b.services || []).filter((k) => !keepSvc.has(k)));
      const dropFlags = new Set((b.addon_flags || []).filter((f) => !keepFlags.has(f)));
      setServiceKeys((prev) => prev.filter((k) => !dropSvc.has(k)));
      setAddonFlags((prev) => prev.filter((f) => !dropFlags.has(f)));
    } else {
      setServiceKeys((prev) => {
        const merged = [...prev];
        for (const k of b.services || []) {
          if (!merged.includes(k) && merged.length < 4) merged.push(k);
        }
        return merged;
      });
      setAddonFlags((prev) => [...new Set([...prev, ...(b.addon_flags || [])])]);
      track("package_added", { bundle: b.key, primary_service: serviceKeys[0] || null });
    }
  };

  // --- Calculator 2.0: tiers, contextual upsells, smart package suggestion ---
  const tierSiblingAddons = (group, svcKey) =>
    [...new Set((TIER_GROUPS[group] || []).filter((t) => t.service === svcKey).flatMap((t) => t.addons || []))];
  // A tier is active when its service is selected AND the selected
  // sibling-tier add-ons match this tier exactly.
  const tierIsActive = (group, t) => {
    if (!serviceKeys.includes(t.service)) return false;
    const sib = tierSiblingAddons(group, t.service);
    const selected = sib.filter((a) => addonFlags.includes(a));
    return selected.length === (t.addons || []).length && (t.addons || []).every((a) => addonFlags.includes(a));
  };
  const toggleTier = (group, t) => {
    if (tierIsActive(group, t)) {
      toggleService(t.service);
      return;
    }
    if (!serviceKeys.includes(t.service)) {
      if (serviceKeys.length >= 4) return;
      setServiceKeys((p) => [...p, t.service]);
    }
    const sib = tierSiblingAddons(group, t.service);
    setAddonFlags((f) => [...new Set([...f.filter((x) => !sib.includes(x)), ...(t.addons || [])])]);
  };
  // Contextual upsell offers for the current selection (CRM-configured rules),
  // merged & deduped, max 3 shown (the premium ceramic card counts as one).
  const upsellItems = useMemo(() => {
    if (!config) return [];
    const rules = config.upsell_rules || [];
    const out = [];
    const seen = new Set();
    for (const svc of selectedServices) {
      for (const it of rules.find((r) => r.service === svc.key)?.items || []) {
        const id = `${it.type}:${it.key}`;
        if (seen.has(id)) continue;
        seen.add(id);
        const meta = it.type === "service"
          ? config.services.find((s) => s.key === it.key)
          : config.addons[it.key];
        if (meta) out.push({ type: it.type, key: it.key, meta });
      }
    }
    return out.slice(0, upsellAddon ? 2 : 3);
  }, [config, selectedServices, upsellAddon]);
  const upsellItemAdded = (it) =>
    it.type === "service"
      ? serviceKeys.includes(it.key)
      : (!!addonSel[it.key] || addonFlags.includes(it.key));
  const toggleUpsellItem = (it) => {
    const added = upsellItemAdded(it);
    if (it.type === "service") {
      // Regular → Deep upgrade: replace the interior level, never stack both.
      if (it.key === "interior-deep" && serviceKeys.includes("interior-regular") && !added) {
        setServiceKeys((p) => p.map((k) => (k === "interior-regular" ? "interior-deep" : k)));
        setAddonFlags((f) => f.filter((x) => !tierSiblingAddons("Detailing", "interior-regular").includes(x)));
      } else if (it.key === "interior-regular" && serviceKeys.includes("interior-deep") && !added) {
        setServiceKeys((p) => p.map((k) => (k === "interior-deep" ? "interior-regular" : k)));
      } else {
        toggleService(it.key);
      }
    } else {
      const a = config.addons[it.key];
      if (a?.mode === "levels") {
        setAddonSel((s) => {
          const n = { ...s };
          if (n[it.key]) delete n[it.key]; else n[it.key] = a.levels[0]?.key;
          return n;
        });
      } else {
        setAddonFlags((f) => (f.includes(it.key) ? f.filter((x) => x !== it.key) : [...f, it.key]));
      }
    }
    track(added ? "upsell_removed" : "upsell_added", { key: it.key, type: it.type, primary_service: serviceKeys[0] || null });
  };
  // "You're almost there" — selection is one step short of a package.
  const almostBundle = useMemo(() => {
    if (!config || serviceKeys.length === 0) return null;
    for (const b of bundles) {
      if (isBundleAdded(b)) continue;
      const missingSvc = (b.services || []).filter((k) => !serviceKeys.includes(k));
      const missingFlags = (b.addon_flags || []).filter((f) => !addonFlags.includes(f));
      const overlap = (b.services || []).filter((k) => serviceKeys.includes(k)).length;
      if (overlap >= 1 && missingSvc.length === 1 && missingFlags.length <= 1
          && serviceKeys.length + missingSvc.length <= 4) {
        const svc = config.services.find((s) => s.key === missingSvc[0]);
        if (svc) {
          return { bundle: b, missingSvc: svc, missingFlag: missingFlags[0] ? config.addons[missingFlags[0]] : null };
        }
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, bundles, serviceKeys, addonFlags]);
  const completeBundle = () => {
    if (!almostBundle) return;
    const b = almostBundle.bundle;
    setServiceKeys((prev) => {
      const merged = [...prev];
      for (const k of b.services || []) if (!merged.includes(k) && merged.length < 4) merged.push(k);
      return merged;
    });
    setAddonFlags((prev) => [...new Set([...prev, ...(b.addon_flags || [])])]);
    track("package_added", { bundle: b.key, via: "smart_recommendation", primary_service: serviceKeys[0] || null });
  };
  // Analytics: packages / upsells viewed — once per session each.
  useEffect(() => {
    if (!config || !(year && make && model)) return;
    for (const b of bundles) {
      if (!viewedRef.current.has(`pv:${b.key}`)) {
        viewedRef.current.add(`pv:${b.key}`);
        track("package_viewed", { bundle: b.key, primary_service: serviceKeys[0] || null });
      }
    }
    for (const it of upsellItems) {
      const id = `uv:${it.type}:${it.key}`;
      if (!viewedRef.current.has(id)) {
        viewedRef.current.add(id);
        track("upsell_viewed", { key: it.key, type: it.type, primary_service: serviceKeys[0] || null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, year, make, model, bundles, upsellItems]);

  // fetch real availability when a date is picked
  useEffect(() => {
    if (!date || serviceKeys.length === 0) { setSlots(null); setTime(""); return; }
    let cancelled = false;
    setSlotsLoading(true);
    api.get("/booking/slots", { params: {
      date, service_keys: serviceKeys.join(","),
      ...(addonFlags.length ? { addon_flags: addonFlags.join(",") } : {}),
    } })
      .then(({ data }) => { if (!cancelled) setSlots(data); })
      .catch(() => { if (!cancelled) setSlots(null); })
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => { cancelled = true; };
  }, [date, serviceKeys, addonFlags]);

  // server-side quote whenever inputs are complete
  useEffect(() => {
    if (!year || !make || !model || serviceKeys.length === 0) { setQuote(null); return; }
    const missingOption = selectedServices.some((s) => s.options?.length && !serviceOptions[s.key]);
    if (missingOption) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      api.post("/pricing/quote", {
        year: Number(year), make, model, service_keys: serviceKeys,
        service_options: serviceOptions,
        addon_selections: addonSel, addon_flags: addonFlags,
        bundles: payloadBundles.map((b) => b.key),
      })
        .then(({ data }) => { if (!cancelled) setQuote(data); })
        .catch(() => { if (!cancelled) setQuote(null); })
        .finally(() => !cancelled && setQuoting(false));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [year, make, model, serviceKeys, serviceOptions, addonSel, addonFlags]);

  if (!config) return null;

  const present = [...new Set(config.services.filter((s) => s.listed !== false).map((s) => s.group))];

  // Regular service card (used in step 03 grids).
  const renderServiceCard = (s) => {
    const lbl = vclass ? priceLabel(s, vclass) : fromLabel(s);
    const active = serviceKeys.includes(s.key);
    const promo = config.promo;
    const eligible = promoEligible(promo, s.key);
    const classPrice = s.prices?.[vclass] ?? s.flat_price ?? null;
    return (
      <button key={s.key} type="button" onClick={() => toggleService(s.key)}
        data-testid={`calc-service-${s.key}`}
        className={`relative text-left px-4 py-3 rounded-xl border transition-colors ${
          active ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
            : "border-white/10 text-white/80 hover:border-white/30 hover:text-white"}`}>
        {active && (
          <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-white text-[#1F5EFF] flex items-center justify-center">
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
        )}
        <div className="text-sm font-medium">{s.name}</div>
        {s.subtitle && (
          <div className={`text-[11px] mt-0.5 leading-snug ${active ? "text-white/70" : "text-white/40"}`}
            data-testid={`calc-subtitle-${s.key}`}>
            {s.subtitle}
          </div>
        )}
        <span className="inline-flex flex-wrap gap-1.5">
          {s.featured && (
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 font-mono-alt text-[9px] uppercase tracking-widest">
              Most Popular
            </span>
          )}
          {eligible && (
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 font-mono-alt text-[9px] uppercase tracking-widest"
              data-testid={`calc-promo-badge-${s.key}`}>
              {pctFor(s.key, classPrice)}% OFF
            </span>
          )}
          {s.key === "full-detail" && fullSaving > 0 && (
            <>
              <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-[#1F5EFF]/15 border border-[#1F5EFF]/50 text-[#8db2ff] font-mono-alt text-[9px] uppercase tracking-widest"
                data-testid="calc-best-value-full-detail">
                Best Value
              </span>
              <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 font-mono-alt text-[9px] uppercase tracking-widest"
                data-testid="calc-save-badge-full-detail">
                Save {money(fullSaving)}
              </span>
            </>
          )}
        </span>
        <div className={`text-[11px] mt-1 tabular-nums ${active ? "text-white/85" : lbl.kind === "custom" ? "text-amber-300/90" : "text-[#8db2ff]"}`}
          data-testid={`calc-price-${s.key}`}>
          {eligible && classPrice != null ? (
            <>
              <span className="line-through text-white/30 mr-1.5">{money(classPrice)}</span>
              <span className={active ? "text-white" : "text-emerald-300"}>{money(promoPrice(classPrice, pctFor(s.key, classPrice)))}</span>
            </>
          ) : eligible && s.options?.length ? (
            <>
              <span className="line-through text-white/30 mr-1.5">{lbl.text}</span>
              <span className={active ? "text-white" : "text-emerald-300"}>
                {s.options.map((o) => `${money(promoPrice(o.price, pctFor(s.key, null)))} ${o.key}`).join(" / ")}
              </span>
            </>
          ) : lbl.text}
        </div>
      </button>
    );
  };
  const groups = present.sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a), ib = GROUP_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  // Full Interior + Exterior Detail bundle saving vs booking both separately
  const price_ = (k) => config.services.find((s) => s.key === k)?.prices?.[vclass];
  const _i = price_("interior-regular") ?? price_("interior-detail"), _e = price_("exterior-detail"), _f = price_("full-detail");
  const fullSaving = (vclass && _i != null && _e != null && _f != null) ? Math.max(0, _i + _e - _f) : 0;
  const vehicleReady = year && make && model;
  const bookingCfg = config.booking || {};
  const todayStr = new Date().toLocaleDateString("en-CA");
  const maxDateStr = new Date(Date.now() + (bookingCfg.horizon_days || 30) * 864e5).toLocaleDateString("en-CA");
  // Grand Opening: dates before the opening day are not selectable at all
  const minDateStr = bookingCfg.min_date && bookingCfg.min_date > todayStr ? bookingCfg.min_date : todayStr;

  const book = () => {
    openQuote({
      service: quote?.service_name || selectedServices.map((s) => s.name).join(" + "),
      vehicle: `${year} ${make} ${model}`,
      booking: {
        year: Number(year), make, model, service_keys: serviceKeys,
        service_options: serviceOptions,
        addon_selections: addonSel, addon_flags: addonFlags,
        bundles: payloadBundles.map((b) => b.key),
        ...(date && time ? { date, time } : {}),
      },
      quote,
    });
  };

  return (
    <section id="calculator" data-testid="calculator-section"
      className="relative py-24 md:py-36 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Instant Estimate</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.02]">
            Price Calculator.{" "}
            <span className="text-white/50">Priced for your exact vehicle.</span>
          </h2>
          <p className="text-white/50 max-w-sm text-sm">
            Tell us your vehicle — we size it automatically and show real prices, not guesses.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Configurator */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="lg:col-span-7 bg-[#121216] border border-white/10 rounded-2xl p-6 md:p-8">

            {/* 01 Vehicle */}
            <div>
              <div className="font-mono-alt text-[10px] text-white/40 mb-3">01 — Your Vehicle</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <SearchSelect value={year} onChange={setYear} options={years} placeholder="Year" testid="calc-year" />
                <SearchSelect value={make} onChange={setMake} options={makes} placeholder="Make" disabled={!year} testid="calc-make" />
                <SearchSelect value={model} onChange={setModel} options={models} placeholder={modelsLoading ? "Loading…" : "Model"} disabled={!make || modelsLoading} loading={modelsLoading} testid="calc-model" />
              </div>
              {classified && (
                <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-emerald-300" data-testid="calc-class-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Vehicle sized automatically — prices below are for your {vehicleLabel(year, make, model)}
                </div>
              )}
            </div>

            {/* 02 — What does your vehicle need? */}
            <div className="mt-8">
              <div className="font-mono-alt text-[10px] text-white/40 mb-3">02 — What does your vehicle need?</div>
              {(() => {
                const ppfCategoryButton = (
                  <button key="ppf" type="button" onClick={() => setActiveGroup(activeGroup === "ppf" ? null : "ppf")}
                    data-testid="calc-category-ppf"
                    aria-expanded={activeGroup === "ppf"}
                    className={`text-left p-3.5 rounded-xl border transition-colors ${
                      activeGroup === "ppf" ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                        : "border-white/10 text-white/80 hover:border-white/30 hover:text-white"}`}>
                    <div className="text-sm font-medium leading-tight">Paint Protection Film</div>
                    <div className={`text-[10px] mt-1 leading-snug ${activeGroup === "ppf" ? "text-white/70" : "text-white/35"}`}>
                      PPF packages, panels & windshield film
                    </div>
                  </button>
                );
                return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="calc-categories">
                {ppfFirst && ppfCategoryButton}
                {groups.map((g) => {
                  const meta = CATEGORY_META[g] || { label: g, blurb: "" };
                  const sel = config.services.filter((s) => s.group === g && serviceKeys.includes(s.key)).length;
                  const isActive = activeGroup === g;
                  return (
                    <button key={g} type="button" onClick={() => setActiveGroup(isActive ? null : g)}
                      data-testid={`calc-category-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      aria-expanded={isActive}
                      className={`text-left p-3.5 rounded-xl border transition-colors ${
                        isActive ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                          : "border-white/10 text-white/80 hover:border-white/30 hover:text-white"}`}>
                      <div className="text-sm font-medium leading-tight">{meta.label}</div>
                      <div className={`text-[10px] mt-1 leading-snug ${isActive ? "text-white/70" : "text-white/35"}`}>{meta.blurb}</div>
                      {sel > 0 && (
                        <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full font-mono-alt text-[9px] ${
                          isActive ? "bg-white/15 text-white" : "bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff]"}`}>
                          {sel} selected
                        </span>
                      )}
                    </button>
                  );
                })}
                {UNAVAILABLE_CATEGORIES.map((c) => (
                  <div key={c.key} data-testid={`calc-category-${c.key}`}
                    className="text-left p-3.5 rounded-xl border border-white/10 bg-black/20 cursor-not-allowed">
                    <div className="text-sm font-medium text-white/45">{c.label}</div>
                    <div className="text-[9px] mt-1.5 text-amber-300/60 font-mono-alt uppercase tracking-widest">{c.blurb}</div>
                  </div>
                ))}
                {/* PPF + Windshield film — dedicated builder (packages, panels, sides) */}
                {!ppfFirst && ppfCategoryButton}
              </div>
                );
              })()}
              {ppfFirst && (
                <div className="mt-2 text-[10px] text-white/30" data-testid="calc-ppf-first-note">
                  PPF is pre-opened below — the other categories stay optional and never interrupt your coverage setup.
                </div>
              )}
            </div>

            {/* PPF builder — packages, individual panels, windshield film */}
            {activeGroup === "ppf" && (
              <div className="mt-6" data-testid="calc-ppf-step">
                <div className="font-mono-alt text-[10px] text-white/40 mb-3">
                  03 — Paint Protection Film <span className="text-white/25">(Veltik Clear PPF PRO 60 · CAD before GST)</span>
                </div>
                <PpfBuilder
                  year={year} make={make} model={model}
                  vehicleClass={classified?.vehicle_class || null}
                  onChange={(sel) => setPpfConfigured(Boolean(sel && (sel.package || sel.windshield || (sel.extra_parts || []).length)))}
                  initial={ppfPreset?.payload}
                  initialNonce={ppfPreset?.n || 0}
                  onBook={(selection) => {
                    try {
                      sessionStorage.setItem("wtd_ppf_prefill", JSON.stringify({
                        selection, vehicle: { year, make, model },
                      }));
                    } catch { /* sessionStorage unavailable — user re-picks on /book */ }
                    track("ppf_book_handoff", { package: selection.package || null, windshield: selection.windshield });
                    navigate("/book");
                  }}
                />
              </div>
            )}

            {/* 03 — Primary service (Good / Better / Best where it makes sense) */}
            {activeGroup && activeGroup !== "ppf" && (
              <div className="mt-6" data-testid="calc-services-step">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-alt text-[10px] text-white/40">
                    03 — {CATEGORY_META[activeGroup]?.label || activeGroup} <span className="text-white/25">(up to 4 services total)</span>
                  </div>
                  {serviceKeys.length > 0 && (
                    <div className="font-mono-alt text-[10px] text-[#8db2ff]" data-testid="calc-selected-count">
                      {serviceKeys.length} selected
                    </div>
                  )}
                </div>
                {TIER_GROUPS[activeGroup] && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="calc-tiers">
                    {TIER_GROUPS[activeGroup].map((t) => {
                      const svc = config.services.find((s) => s.key === t.service);
                      if (!svc) return null;
                      const active = tierIsActive(activeGroup, t);
                      const classPrice = svc.prices?.[vclass] ?? svc.flat_price ?? null;
                      const eligible = promoEligible(config.promo, t.service);
                      const pct = pctFor(t.service, classPrice);
                      let addonSum = 0;
                      for (const ak of t.addons || []) {
                        const a = config.addons[ak];
                        const ap = vclass ? a?.prices?.[vclass] ?? a?.price ?? null : null;
                        if (ap != null) addonSum += ap;
                      }
                      const showPrice = classPrice != null && (t.addons || []).every((ak) => {
                        const a = config.addons[ak];
                        return (a?.prices?.[vclass] ?? a?.price) != null;
                      });
                      return (
                        <button key={t.label} type="button" onClick={() => toggleTier(activeGroup, t)}
                          data-testid={`calc-tier-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`relative text-left p-4 rounded-xl border transition-colors ${
                            active ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                              : "border-white/10 text-white/80 hover:border-white/30 hover:text-white"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`px-2 py-0.5 rounded-full font-mono-alt text-[8px] uppercase tracking-widest border ${
                              active ? "bg-white/15 border-white/30 text-white"
                                : t.tag === "Most Popular" ? "bg-amber-400/15 border-amber-400/40 text-amber-300"
                                : "bg-white/5 border-white/15 text-white/50"}`}>
                              {t.tag}
                            </span>
                            {active && (
                              <span className="w-5 h-5 rounded-full bg-white text-[#1F5EFF] flex items-center justify-center">
                                <Check className="w-3 h-3" strokeWidth={3} />
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium mt-2">{t.label}</div>
                          <div className={`text-[11px] mt-0.5 leading-snug ${active ? "text-white/70" : "text-white/40"}`}>{t.hint}</div>
                          <div className={`text-[11px] mt-1.5 tabular-nums ${active ? "text-white/85" : "text-[#8db2ff]"}`}
                            data-testid={`calc-tier-price-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {!vclass || !showPrice ? (
                              <span className="text-white/35">Select your vehicle</span>
                            ) : eligible ? (
                              addonSum > 0 ? (
                                <>
                                  <span className="line-through text-white/30 mr-1.5">{money(classPrice + addonSum)}</span>
                                  <span className={active ? "text-white" : "text-emerald-300"}>{money(promoPrice(classPrice, pct) + addonSum)}</span>
                                  <span className="block mt-1 text-emerald-300 font-mono-alt text-[9px] uppercase" data-testid="calc-tier-base-promo-note">
                                    {pct}% off base correction
                                  </span>
                                  <span className={`block font-mono-alt text-[9px] uppercase tracking-widest ${active ? "text-white/60" : "text-white/40"}`} data-testid="calc-tier-addon-note">
                                    +{money(addonSum)} add-on — special add-on price
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="line-through text-white/30 mr-1.5">{money(classPrice + addonSum)}</span>
                                  <span className={active ? "text-white" : "text-emerald-300"}>{money(promoPrice(classPrice, pct) + addonSum)}</span>
                                  <span className="ml-1.5 text-emerald-300 font-mono-alt text-[9px] uppercase">{pct}% off</span>
                                </>
                              )
                            ) : money(classPrice + addonSum)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {GROUP_NOTES[activeGroup] && TIER_GROUPS[activeGroup] && (
                  <p className="-mt-2 mb-4 text-[11px] text-white/45 leading-snug" data-testid="calc-group-note">
                    {GROUP_NOTES[activeGroup]}
                  </p>
                )}
                {(() => {
                  const tiered = new Set((TIER_GROUPS[activeGroup] || []).map((t) => t.service));
                  // listed === false: package/CRM-only service (e.g. decon — sold as an add-on), never a standalone card
                  const rest = config.services.filter((s) => s.group === activeGroup && !tiered.has(s.key) && s.listed !== false);
                  if (rest.length === 0) return null;
                  return (
                    <>
                      {TIER_GROUPS[activeGroup] && (
                        <div className="font-mono-alt text-[9px] text-white/30 uppercase tracking-widest mb-2">More in this category</div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rest.map(renderServiceCard)}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* 04 — Recommended for your vehicle (contextual, max 3). On the PPF page these are optional cross-sells that wait for the PPF configuration. */}
            {(!ppfFirst || ppfConfigured) && serviceKeys.length > 0 && (upsellAddon || upsellItems.length > 0) && (
              <div className="mt-8" data-testid="calc-upsells">
                <div className="font-mono-alt text-[10px] text-white/40 mb-3">04 — Recommended for your vehicle</div>
                {upsellAddon && (
                  <UpsellCard
                    addon={upsellAddon}
                    vclass={vclass}
                    added={addonFlags.includes(upsellAddon.key)}
                    promoPct={upsellAddon.promo_discount && promoEligible(config?.promo, "full-detail")
                      ? (promoPcts["full-detail"] ?? (Number(config?.promo?.percent) || 50))
                      : null}
                    pricePrefix={upsellAddon.promo_discount ? "Add to your Full Detail — " : "Add with Paint Correction — "}
                    onToggle={() => {
                      const wasAdded = addonFlags.includes(upsellAddon.key);
                      setAddonFlags((f) =>
                        wasAdded ? f.filter((x) => x !== upsellAddon.key) : [...f, upsellAddon.key]
                      );
                      track(wasAdded ? "upsell_removed" : "upsell_added",
                        { key: upsellAddon.key, type: "addon", premium: true, primary_service: serviceKeys[0] || null });
                    }}
                  />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {upsellItems.map((it) => {
                    const added = upsellItemAdded(it);
                    const isSvc = it.type === "service";
                    const name = isSvc ? it.meta.name : it.meta.label;
                    let priceTxt = "";
                    if (isSvc) {
                      priceTxt = (vclass ? priceLabel(it.meta, vclass) : fromLabel(it.meta)).text;
                    } else {
                      const a = it.meta;
                      const p = a.prices?.[vclass] ?? (a.mode === "levels" ? a.levels?.[0]?.price : a.price);
                      const minP = !vclass && a.prices ? Math.min(...Object.values(a.prices).filter((v) => v != null)) : null;
                      priceTxt = p != null ? `+${money(p)}${a.mode === "levels" && a.levels?.length > 1 ? "+" : ""}`
                        : Number.isFinite(minP) ? `From +${money(minP)}` : "Custom";
                    }
                    return (
                      <button key={`${it.type}:${it.key}`} type="button" onClick={() => toggleUpsellItem(it)}
                        data-testid={`calc-upsell-${it.key}`}
                        className={`relative text-left px-4 py-3 rounded-xl border transition-colors ${
                          added ? "bg-emerald-500/12 border-emerald-400/50 text-white"
                            : "border-dashed border-[#1F5EFF]/40 bg-[#1F5EFF]/5 text-white/85 hover:border-[#1F5EFF]/70"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium">{name}</div>
                          <span className={`shrink-0 text-[11px] tabular-nums ${added ? "text-emerald-300" : "text-[#8db2ff]"}`}>
                            {added ? "Added ✓" : priceTxt}
                          </span>
                        </div>
                        {(it.type === "service" && it.key === "interior-deep" && serviceKeys.includes("interior-regular")) ? (
                          <div className="text-[10px] mt-0.5 text-white/40 leading-snug" data-testid="calc-upsell-deep-note">
                            Optional upgrade — Regular is for routine maintenance. Deep is full interior shampoo extraction & steam cleaning for stains, heavily soiled carpets or deep fabric cleaning.
                          </div>
                        ) : (it.meta.subtitle || it.meta.note) && (
                          <div className="text-[10px] mt-0.5 text-white/40 leading-snug">
                            {(it.meta.subtitle || it.meta.note || "").slice(0, 90)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 05 — Popular Packages (live prices, deduped, never stacked with promos). On the PPF page: optional cross-sells shown only after the PPF configuration is complete. */}
            {(!ppfFirst || ppfConfigured) && bundles.length > 0 && (
              <div className="mt-8">
                <div className="font-mono-alt text-[10px] text-white/40 mb-1">05 — Popular Packages</div>
                {promoActive(config.promo) ? (
                  <p className="text-[11px] text-amber-300/70 mb-3" data-testid="bundles-go-note">
                    Grand Opening pricing automatically applied. Bundle discounts cannot be combined with Grand Opening offers.
                  </p>
                ) : (
                  <p className="text-[11px] text-white/40 mb-3">Package pricing applied automatically when you add a package.</p>
                )}
                {almostBundle && (
                  <div className="mb-3 rounded-2xl border border-emerald-400/40 bg-emerald-400/8 p-4" data-testid="calc-almost-bundle">
                    <div className="flex items-center gap-2 font-mono-alt text-[9px] uppercase tracking-widest text-emerald-300 mb-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> You're almost there
                    </div>
                    <div className="text-white text-sm leading-snug">
                      Add <span className="font-medium">{almostBundle.missingSvc.name}</span>
                      {almostBundle.missingFlag ? <> + <span className="font-medium">{almostBundle.missingFlag.label}</span></> : null}
                      {" "}to complete <span className="font-medium">{almostBundle.bundle.name}</span>
                      {(() => {
                        const mp = vclass ? almostBundle.missingSvc.prices?.[vclass] ?? null : null;
                        if (mp == null) return null;
                        const mel = promoEligible(config.promo, almostBundle.missingSvc.key);
                        return (
                          <span className="text-emerald-300 tabular-nums">
                            {" — "}{mel ? money(promoPrice(mp, pctFor(almostBundle.missingSvc.key, mp))) : `+${money(mp)}`}
                          </span>
                        );
                      })()}
                    </div>
                    <button type="button" onClick={completeBundle} data-testid="calc-complete-bundle"
                      className="mt-2.5 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/30 transition-colors">
                      Complete the package
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {bundles.map((b) => {
                    const added = isBundleAdded(b);
                    const info = bundlePriceInfo(config, b, vclass, selForPromo);
                    const names = (b.services || [])
                      .map((k) => config.services.find((s) => s.key === k)?.name)
                      .filter(Boolean)
                      .concat((b.addon_flags || []).map((f) => config.addons[f]?.label).filter(Boolean));
                    return (
                      <div key={b.key} data-testid={`calc-bundle-${b.key}`}
                        className={`rounded-2xl border p-4 transition-colors ${
                          added ? "border-[#1F5EFF] bg-[#1F5EFF]/12" : "border-white/10 bg-black/30 hover:border-[#1F5EFF]/40"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-display text-base text-white leading-snug">{b.name}</div>
                          {info?.promoApplied && (
                            <span className="shrink-0 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 font-mono-alt text-[8px] uppercase tracking-widest">
                              Grand Opening Applied
                            </span>
                          )}
                        </div>
                        {b.tagline && <div className="text-[11px] text-white/45 mt-0.5">{b.tagline}</div>}
                        <ul className="mt-2.5 space-y-1">
                          {names.map((n) => (
                            <li key={n} className="flex items-start gap-1.5 text-[11px] text-white/65">
                              <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#8db2ff]" strokeWidth={2.5} />
                              {n}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-3 min-h-[34px]">
                          {!vclass ? (
                            <div className="text-[11px] text-white/35">Select your vehicle for package pricing</div>
                          ) : info ? (
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-white/35 text-xs line-through tabular-nums">{money(info.regular)}</span>
                              <span className="text-white font-medium tabular-nums" data-testid={`bundle-price-${b.key}`}>{money(info.final)}</span>
                              {!info.promoApplied && (
                                <span className="text-emerald-300 font-mono-alt text-[9px] uppercase tracking-widest">Save {info.pct}%</span>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11px] text-amber-300/80">Custom quote for your vehicle</div>
                          )}
                        </div>
                        <button type="button" onClick={() => toggleBundle(b)}
                          data-testid={`calc-bundle-toggle-${b.key}`}
                          className={`mt-2 w-full px-4 py-2.5 rounded-xl text-xs font-semibold transition-colors ${
                            added
                              ? "bg-emerald-500/15 border border-emerald-400/50 text-emerald-300 hover:bg-red-500/10 hover:border-red-400/40 hover:text-red-300"
                              : "bg-[#1F5EFF] text-white hover:bg-[#8db2ff] hover:text-black"}`}>
                          {added ? "✓ Package Added — tap to remove" : "Add Package"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 03 Options (fixed-price service variants) — per selected service */}
            {selectedServices.filter((s) => s.options?.length > 0).map((svc) => (
              <div className="mt-8" key={`opts-${svc.key}`}>
                <div className="font-mono-alt text-[10px] text-white/40 mb-3">
                  06 — Options{selectedServices.length > 1 ? ` · ${svc.name}` : ""}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {svc.options.map((o) => (
                    <button key={o.key}
                      onClick={() => setServiceOptions((so) => ({ ...so, [svc.key]: so[svc.key] === o.key ? undefined : o.key }))}
                      data-testid={`calc-option-${svc.key}-${o.key}`}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                        serviceOptions[svc.key] === o.key ? "bg-[#1F5EFF]/15 border-[#1F5EFF] text-white"
                          : "border-white/10 text-white/70 hover:border-white/30"}`}>
                      <span>{o.label}</span>
                      <span className="tabular-nums">{money(o.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* 04 Add-ons — one section per selected service */}
            {addonsByService.length > 0 && (
              <div className="mt-8">
                <div className="font-mono-alt text-[10px] text-white/40 mb-3">
                  07 — Add-ons & Condition
                </div>
                <div className="space-y-6">
                  {addonsByService.map(({ service: svc, addons: list }) => (
                    <div key={`addons-${svc.key}`}>
                      {selectedServices.length > 1 && (
                        <div className="text-[#8db2ff] font-mono-alt text-[10px] uppercase tracking-widest mb-2"
                          data-testid={`calc-addons-for-${svc.key}`}>
                          For {svc.name}
                        </div>
                      )}
                      <div className="space-y-5">
                        {list.map((a) => (
                          <div key={a.key}>
                            <div className="text-white/70 text-sm mb-2">{a.label}</div>
                            {a.mode === "levels" ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {a.levels.map((l) => {
                                  const active = addonSel[a.key] === l.key;
                                  return (
                                    <button key={l.key}
                                      onClick={() => setAddonSel((s) => ({ ...s, [a.key]: active ? undefined : l.key }))}
                                      data-testid={`calc-addon-${a.key}-${l.key}`}
                                      className={`flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                                        active ? "bg-[#1F5EFF]/15 border-[#1F5EFF] text-white"
                                          : "border-white/10 text-white/70 hover:border-white/30"}`}>
                                      <span>{l.label}{l.note ? <span className="text-white/40 text-[11px]"> · {l.note}</span> : null}</span>
                                      <span className="tabular-nums">
                                        {addonPrice(l, vclass).text}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : a._included ? (
                              <div data-testid={`calc-addon-${a.key}`}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-emerald-400/20 bg-emerald-400/5 text-sm">
                                <span className="flex items-center gap-3">
                                  <span className="w-4 h-4 rounded border flex items-center justify-center bg-emerald-500/20 border-emerald-400/40">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                      <path d="M5 12l5 5L20 7" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                  <span className="text-white/80">{a.label}</span>
                                </span>
                                <span className="text-emerald-300 text-[11px] font-mono-alt uppercase tracking-wider">Included</span>
                              </div>
                            ) : (
                              <button
                                onClick={() => setAddonFlags((f) => f.includes(a.key) ? f.filter((x) => x !== a.key) : [...f, a.key])}
                                data-testid={`calc-addon-${a.key}`}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${
                                  addonFlags.includes(a.key) ? "bg-[#1F5EFF]/15 border-[#1F5EFF] text-white"
                                    : "border-white/10 text-white/70 hover:border-white/30"}`}>
                                <span className="flex items-center gap-3">
                                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${addonFlags.includes(a.key) ? "bg-[#1F5EFF] border-[#1F5EFF]" : "border-white/30"}`}>
                                    {addonFlags.includes(a.key) && (
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 12l5 5L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </span>
                                  {a.label}
                                </span>
                                <span className="tabular-nums text-white/60 text-xs">
                                  {a.service_ref
                                    ? (() => { const rs = config.services.find((s) => s.key === a.service_ref); return rs && vclass ? priceLabel(rs, vclass).text : "priced for your vehicle"; })()
                                    : addonPrice(a, vclass).text}
                                </span>
                              </button>
                            )}
                            {a.note && <div className="mt-1.5 text-[11px] text-white/40 leading-relaxed">{a.note}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {addonFlags.includes("leather_protection") && addonFlags.includes("fabric_protection") && !serviceKeys.some((k) => promoEligible(config.promo, k)) && (() => {
                    const line = quote?.addons?.find((x) => x.key === "interior_bundle");
                    return (
                      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-emerald-400/5 border border-emerald-400/25 text-emerald-200/90 text-[12px] leading-relaxed"
                        data-testid="calc-interior-bundle-hint">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-300" />
                        <span>
                          <span className="font-medium text-emerald-200">Complete Interior Protection</span> — leather + fabric together qualify for a bundle discount
                          {line ? <> — you save <span className="tabular-nums font-medium">{money(Math.abs(line.price))}</span></> : ""}.
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* 08 Date & time */}
            {serviceKeys.length > 0 && (
              <div className="mt-8">
                <div className="font-mono-alt text-[10px] text-white/40 mb-3">
                  08 — Date & Time <span className="text-white/25">(optional — reserve a real slot)</span>
                </div>
                <DateField value={date} min={minDateStr} max={maxDateStr}
                  onChange={(v) => { setDate(v); setTime(""); }}
                  className={sel} testid="calc-date" placeholder="Pick a date" />
                {date && (
                  slotsLoading ? (
                    <div className="mt-3 text-white/40 text-sm flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Checking availability…
                    </div>
                  ) : slots?.closed ? (
                    <div className="mt-3 text-amber-300/80 text-sm" data-testid="calc-closed">
                      {slots?.reason === "before_opening"
                        ? `We open ${new Date(slots.opens + "T12:00:00").toLocaleDateString("en-CA", { month: "long", day: "numeric" })} — online booking starts that day.`
                        : new Date(date + "T12:00:00").getDay() === 0
                          ? `Sundays are by appointment only — call ${CONTACT.phone} and we'll book you in.`
                          : "We're closed on this day — please pick another date."}
                    </div>
                  ) : slots ? (
                    <>
                      <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="calc-slots">
                        {slots.slots.map((s) => (
                          <button key={s.time} type="button" disabled={!s.available}
                            onClick={() => setTime(time === s.time ? "" : s.time)}
                            data-testid={`calc-slot-${s.time}`}
                            className={`px-2 py-2.5 rounded-xl border text-sm tabular-nums transition-colors ${
                              time === s.time
                                ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                                : s.available
                                  ? "border-white/10 text-white/80 hover:border-white/30 hover:text-white"
                                  : "border-white/5 text-white/25 line-through cursor-not-allowed"}`}>
                            {s.time}
                          </button>
                        ))}
                      </div>
                      {slots.slots.every((s) => !s.available) && (
                        <div className="mt-3 text-amber-300/80 text-sm" data-testid="calc-full">
                          This day is fully booked — please pick another date.
                        </div>
                      )}
                      <div className="mt-2 text-[11px] text-white/40 leading-relaxed">
                        This service takes about {(slots.duration_min / 60).toFixed(1).replace(/\.0$/, "")} h.
                        {time ? ` Reserved: ${date} at ${time}.` : " Pick a time and we'll have a bay ready for you."}
                      </div>
                    </>
                  ) : null
                )}
              </div>
            )}

            {service?.note && (
              <div className="mt-6 p-3.5 rounded-xl bg-amber-400/5 border border-amber-400/20 text-amber-200/80 text-[12px] leading-relaxed" data-testid="calc-service-note">
                {service.note}
              </div>
            )}
          </motion.div>

          {/* Result */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-gradient-to-br from-[#17171c] to-[#131319] border border-white/10 rounded-2xl p-6 md:p-8" data-testid="calc-summary">
              <div className="font-mono-alt text-[11px] text-[#1F5EFF]">Your Estimate</div>

              {!vehicleReady && (
                <div className="mt-6 text-white/45 text-sm flex items-start gap-3">
                  <Car className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Select your vehicle's year, make and model — prices will adjust to it automatically.
                </div>
              )}
              {vehicleReady && serviceKeys.length === 0 && (
                <div className="mt-6 text-white/45 text-sm">
                  Vehicle: <span className="text-white">{vehicleLabel(year, make, model)}</span>. Now pick one or more services.
                </div>
              )}

              {vehicleReady && serviceKeys.length > 0 && (
                quoting && !quote ? (
                  <div className="mt-6 text-white/40 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Calculating…
                  </div>
                ) : quote ? (
                  <>
                    <div className="mt-4">
                      <div className="text-white/60 text-sm">{quote.service_name}</div>
                      <div className="text-white/40 text-xs mt-0.5">
                        {quote.vehicle.year} {quote.vehicle.make} {quote.vehicle.model}
                      </div>
                      {date && time && (
                        <div className="mt-2 text-[13px] text-[#8db2ff]" data-testid="calc-summary-slot">
                          Slot: {date} at {time} · ~{Math.round((quote.duration_min || 60) / 60 * 10) / 10}h
                        </div>
                      )}
                      <div className="mt-4 font-display text-5xl md:text-6xl text-white tabular-nums leading-none" data-testid="calc-total">
                        {quote.price_type === "custom" ? (
                          <span className="text-3xl md:text-4xl">Custom Quote Required</span>
                        ) : quote.price_type === "starting" ? (
                          <>Starting at {money(quote.total)}</>
                        ) : (
                          money(quote.total)
                        )}
                      </div>
                      {quote.price_type === "starting" && (
                        <div className="mt-2 text-white/45 text-xs">Final price confirmed after inspection.</div>
                      )}
                      {quote.price_type === "custom" && (
                        <div className="mt-2 text-white/45 text-xs">This vehicle or condition needs a quick look — we'll quote it exactly.</div>
                      )}
                    </div>

                    <div className="mt-6 border-t border-white/10 pt-5 space-y-2 text-sm">
                      {(quote.services || []).map((s, i) => (
                        <div key={i}>
                          <div className="flex justify-between" data-testid={`calc-summary-service-${i}`}>
                            <span className="text-white/70">{s.name}{s.option ? ` — ${s.option.label}` : ""}</span>
                            <span className="text-white tabular-nums">
                              {s.price != null ? (s.price_type === "starting" ? `from ${money(s.price)}` : money(s.price)) : "Custom"}
                            </span>
                          </div>
                          {s.discount && (
                            <div className="flex justify-between text-[12px] mt-0.5" data-testid={`calc-summary-discount-${i}`}>
                              <span className="text-emerald-300/80 pl-3">
                                {s.discount.type === "grand_opening_main"
                                  ? `Grand Opening −${s.discount.percent}%`
                                  : `Additional service −${s.discount.percent}%`}
                              </span>
                              <span className="text-emerald-300 tabular-nums">−{money(s.discount.amount)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {quote.addons.filter((a) => !a.key.startsWith("promo_") && a.key !== "bundle_discount").map((a, i) => (
                        <div key={i}>
                          <div className="flex justify-between">
                            <span className="text-white/70">{a.label}</span>
                            <span className={`tabular-nums ${a.price < 0 ? "text-emerald-300" : "text-white"}`}>
                              {a.custom ? "Inspection" : a.price < 0 ? `−${money(Math.abs(a.price))}` : `+${money(a.price)}`}
                            </span>
                          </div>
                          {a.discount && (
                            <div className="flex justify-between text-[12px] mt-0.5" data-testid={`calc-summary-addon-discount-${i}`}>
                              <span className="text-emerald-300/80 pl-3">
                                {a.discount.type === "grand_opening_main"
                                  ? `Grand Opening −${a.discount.percent}%`
                                  : `Additional service −${a.discount.percent}%`}
                              </span>
                              <span className="text-emerald-300 tabular-nums">−{money(a.discount.amount)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {quote.addons.filter((a) => a.key === "bundle_discount").map((a, i) => (
                        <div key={`bd-${i}`} className="flex justify-between" data-testid="calc-summary-bundle-discount">
                          <span className="text-emerald-300/80 pl-3">{a.label}</span>
                          <span className="text-emerald-300 tabular-nums">−{money(Math.abs(a.price))}</span>
                        </div>
                      ))}
                      {quote.discounts?.total_discount > 0 && (
                        <>
                          <div className="flex justify-between pt-2 mt-1 border-t border-white/10" data-testid="calc-summary-regular">
                            <span className="text-white/50">Regular total</span>
                            <span className="text-white/50 tabular-nums">{money(quote.discounts.regular_subtotal)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="calc-summary-savings">
                            <span className="text-emerald-300 font-medium">You save</span>
                            <span className="text-emerald-300 font-medium tabular-nums">−{money(quote.discounts.total_discount)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    <button onClick={book} data-testid="calc-book"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-black font-semibold hover:bg-[#1F5EFF] hover:text-white transition-colors">
                      {quote.price_type === "custom" ? "Request Custom Quote" : date && time ? `Book ${date} · ${time}` : "Book This Quote"} <ArrowRight className="w-4 h-4" />
                    </button>
                    {!(date && time) && quote.price_type !== "custom" && (
                      <p className="mt-2 text-[11px] text-[#8db2ff]/80 text-center" data-testid="calc-book-hint">
                        You'll pick the date & time at the next step.
                      </p>
                    )}
                    <p className="mt-5 text-[11px] text-white/40 leading-relaxed">
                      Priced for your exact vehicle. No hidden charges — any condition adjustment is always shown before booking.
                    </p>
                  </>
                ) : (
                  <div className="mt-6 text-white/45 text-sm">Adjust your selection to see the price.</div>
                )
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
